import { DesignDto, MenuFolderDto } from "../../generated/dashboard-client";
import "./SortDesigns.scss";
import { useEffect, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";
import { FilterSizesMap } from "../../util/constants";
import { Button, Input } from "antd";
import { AppstoreOutlined, DownOutlined, GiftOutlined, SearchOutlined } from "@ant-design/icons";
import FilterChecked from "../icons/FilterChecked";
import FilterUnchecked from "../icons/FilterUnchecked";

export interface FilterDesignsMenuProps {
  designs: DesignDto[];
  activeFolder: MenuFolderDto | null;
  setDesigns: (designs: DesignDto[]) => void;
  currentFilter: string[];
  setCurrentFilter: (filter: string[]) => void;
  mobile: boolean;
  isOpen: boolean;
  closeMenu: () => void;
}

interface SearchCat {
  cat: string,
  visible?: boolean
}

export default function FilterDesignsMenu({
  designs,
  activeFolder,
  setDesigns,
  currentFilter,
  setCurrentFilter,
  mobile,
  isOpen,
  closeMenu
}: FilterDesignsMenuProps) {
  const ref = useRef(null);
  const [searchCats, setSearchCats] = useState([] as SearchCat[]);
  const [toBeFiltered, setToBeFiltered] = useState([] as string[]);

  const handleClickOutside = (ev: MouseEvent) => {
    closeMenu();
  }
  useOnClickOutside(ref, (ev) => handleClickOutside(ev as MouseEvent));

  useEffect(() => {
    setSearchCats(Object.keys(FilterSizesMap).map(it => {
      return {cat: it, visible: true} as SearchCat;
    }))
  }, []);

  useEffect(() => {
    if (isOpen) {
      setToBeFiltered([...currentFilter]);
    }
  }, [isOpen, currentFilter]);

  function toggleFilterCat(option: string) {
    let filterCats = [...toBeFiltered];
    if (filterCats.includes(option)) {
      filterCats.splice(filterCats.indexOf(option), 1);
    } else {
      if (option === "all-categories") {
        filterCats = ["all-categories"];
      } else {
        if (filterCats.includes("all-categories")) {
          filterCats.splice(filterCats.indexOf("all-categories"), 1);
        }
        filterCats.push(option);
      }
    }
    setToBeFiltered([...filterCats]);
  }

  function onSearchChange(search: string) {
    searchCats.forEach(s => {
      if (s.cat.replaceAll('-', ' ').toLowerCase().includes(search.toLowerCase())) {
        s.visible = true;
      } else {
        s.visible = false;
      }
    })
    setSearchCats([...searchCats]);
  }

  function showAllCategories() {
    let filteredDesigns = [...designs];
    filteredDesigns.forEach((des) => {
      if (activeFolder) {
        des.visible = (des.folders ?? "").indexOf(`${activeFolder.id}`) > -1;
      } else {
        des.visible = (des.folders ?? "") === "";
      }
    });
    setCurrentFilter(["all-categories"]);
    setDesigns([...filteredDesigns]);
    closeMenu();
  }
  
  function onApplyClick() {
    if (toBeFiltered.length === 0) return;
    if (toBeFiltered.includes("all-categories")) {
      showAllCategories();
    } else {
      let filteredDesigns = [...designs];
      filteredDesigns.forEach(function(des) {
        des.visible = false;
        Object.keys(FilterSizesMap).forEach(size => {
          if (toBeFiltered.includes(size) && FilterSizesMap[size].includes(des.size!)) {
            if (activeFolder) {
              des.visible = (des.folders ?? "").indexOf(`${activeFolder.id}`) > -1;
            } else {
              des.visible = true;
            }
          }
        })
      })
      setCurrentFilter([...toBeFiltered]);
      setDesigns([...filteredDesigns]);
      closeMenu();
    }
  }

  const catIcons = [
    <AppstoreOutlined/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/Templates.svg" alt="menu"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/DigiBoard.svg" alt="digi board"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/TableTent.svg" alt="table tent"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/Takeout.svg" alt="takeout"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/HalfPageMenu.svg" alt="half page menu"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/Flyer.svg" alt="flyer"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/KidsMenu.svg" alt="kids menu"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/Poster.svg" alt="poster"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/BusinessCard.svg" alt="business card"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/LoyaltyCard.svg" alt="loyalty card"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/Sticker.svg" alt="sticker"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/Instagram.png" alt="insta post"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/Instagram.png" alt="insta story"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/Facebook.svg" alt="facebook post"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/Facebook.svg" alt="facebook story"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/TabletopInsert.svg" alt="tabletop insert"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/Logo.svg" alt="logo"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/SandwichBoard.svg" alt="sandwich board"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/Label.svg" alt="food label"/>,
    <GiftOutlined/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/CommentCard.svg" alt="comment card"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/FilterProducts/RackCard.svg" alt="rack card"/>,
    <img className="cat-icon" src="https://timber.mhmcdn.com/site/marketing/icons/Templates.svg" alt="menu"/>,
  ]

  return (
    <div className={"filter-designs-menu"} ref={ref}>
      {mobile &&
      <div className="cat-header">
        <span>Category</span>
        <DownOutlined onClick={() => closeMenu()}/>
      </div>}
      <div className="cat-search">
        <Input placeholder="Search Categories" prefix={<SearchOutlined />} onChange={(e) => onSearchChange(e.target.value)} />
      </div>
      {searchCats.filter(it => it.visible).length === 0 && <div className="no-results">
        No categories found.
      </div>}
      {searchCats.filter(it => it.visible).length > 0 && <ul>
        {searchCats.filter(it => it.visible).map((s, index) => {
          return (
            <li onClick={() => toggleFilterCat(s.cat)}>
              {toBeFiltered.includes(s.cat) && <FilterChecked/>}
              {!toBeFiltered.includes(s.cat) && <FilterUnchecked/>}
              {catIcons[index] ? catIcons[index] : <>icon</>}
              {s.cat.replaceAll('-', ' ')}
            </li>
          )
        })}
      </ul>}
      <div className="cat-footer">
        <Button type="primary" className={toBeFiltered.length === 0 ? 'disabled' : ''}  onClick={() => onApplyClick()}>Apply</Button>
      </div>
    </div>
  );
}
